import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api, euro, API } from "@/lib/api";
import { ProductPlaceholder } from "@/components/ProductPlaceholder";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ShieldCheck, MapPin, Leaf, Truck, Phone, PackageCheck, Star, Sprout } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/5d3e454e-4085-403e-80e5-061446169ecd/images/898f603e7ebe1cd7999efcb217103f7003c61d82a1f7f51b66db4ffcd46f9230.png";
const HEBREW = { bet: "ב", aleph: "א", aleph_aleph: "אא" };

const FAQ = [
  { q: "Les sets sont-ils valides hala'hiquement ?", a: "Chaque set est vérifié avant la remise : loulav droit et kasher, etrog contrôlé (pitom, droiture, fraîcheur), hadassim et aravot frais." },
  { q: "Comment se passe la commande ?", a: "Vous remplissez le formulaire de réservation. Nous vous rappelons ensuite pour convenir ensemble du paiement (espèces ou Revolut) et de l'horaire de livraison ou de retrait." },
  { q: "Serai-je livré à temps avant la fête ?", a: "Oui, nous livrons impérativement avant l'entrée de Souccot. Nous fixons l'horaire précis avec vous par téléphone." },
  { q: "La livraison est-elle payante ?", a: "Non, la livraison est offerte partout en Île-de-France. Le retrait dans le 19e est également gratuit." },
];

export default function Home() {
  const { settings } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => {});
  }, []);

  const featured = products.find((p) => p.featured);
  const headline = featured || products[0];
  const single = products.length === 1;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="brand-container grid lg:grid-cols-2 gap-10 items-center py-14 sm:py-20">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-green/10 text-brand-green px-3 py-1 text-xs font-semibold uppercase tracking-widest mb-5">
              <Leaf className="w-3.5 h-3.5" /> Les quatre espèces — Souccot 2026
            </span>
            <h1 className="font-serif text-5xl sm:text-6xl font-bold text-brand-green leading-none tracking-tight">
              Vos sets loulav &amp; etrog,<br />livrés avant la fête.
            </h1>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-green text-white px-4 py-2 text-sm sm:text-base font-semibold">
              <Truck className="w-4 h-4 text-brand-gold" /> Livraison offerte — partout en Île-de-France
            </p>
            <p className="mt-5 text-base sm:text-lg text-brand-green/75 max-w-lg leading-relaxed">
              Réservez votre set en quelques clics. Nous vous rappelons pour convenir du paiement
              (espèces ou Revolut) et de l'horaire de livraison ou de retrait dans le 19e.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/commander" data-testid="hero-cta" className="rounded-full bg-brand-gold text-brand-green px-7 py-4 text-base font-bold hover:bg-brand-goldHover transition-colors shadow-lg shadow-brand-gold/20">
                Réserver mon set
              </Link>
              <Link to="/comparatif" className="text-brand-green font-semibold underline underline-offset-4 hover:text-brand-greenLight">
                Comparer les gammes
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-brand-green/70">
              {[["Livraison offerte", Truck], ["Vendeur local du 19e", MapPin], ["Vérifié kasher", PackageCheck], ["Rappel rapide", Phone]].map(([t, Icon]) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4 text-brand-gold" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={HERO} alt="Set loulav et etrog" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" data-testid="hero-image" />
            {headline && (
              <div className="absolute -bottom-5 left-5 bg-white rounded-2xl shadow-xl px-5 py-3 flex items-center gap-3 animate-soft-pulse">
                <span className="text-xs uppercase tracking-widest text-brand-green/50">À partir de</span>
                <span className="num text-3xl text-brand-green">{euro(Math.min(...products.map((p) => p.price_ttc)))}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="brand-container py-16" id="gammes">
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-green text-center mb-2">
          {single ? "Notre set" : "Trois gammes, un seul objectif : la beauté de la mitsva"}
        </h2>

        {/* QUALITÉ & PROVENANCE */}
        <div className="max-w-5xl mx-auto mt-8 mb-8">
          <div className="relative rounded-3xl overflow-hidden border border-brand-green/15 shadow-lg">
            <div className="absolute inset-0 bg-cover bg-center opacity-45" style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/5d3e454e-4085-403e-80e5-061446169ecd/images/7b10e687c0f073d62751fb33e6e11844900bced5dfc0237ea3cde7e099ffa185.png')" }} />
            <div className="absolute inset-0 bg-brand-sage/75" />
            <div className="relative p-8 sm:p-10">
              <h3 className="font-serif text-3xl sm:text-4xl font-bold text-brand-green text-center">Loulav premium</h3>
              <p className="text-center text-brand-green/70 max-w-2xl mx-auto mt-2 mb-8">
                Des sets de qualité, soigneusement sélectionnés pour un beau Hiddour.
              </p>
              <div className="grid sm:grid-cols-3 gap-5">
                {[
                  [Sprout, "Loulav d'Israël", "Une provenance d'exception, directement d'Israël."],
                  [Leaf, "Hadassim d'Égypte", "Des hadassim sélectionnés, en provenance d'Égypte."],
                  [ShieldCheck, "Qualité garantie", "Chaque set est vérifié avant la remise."],
                ].map(([Icon, title, desc]) => (
                  <div key={title} className="text-center sm:text-left">
                    <div className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center mx-auto sm:mx-0 mb-3">
                      <Icon className="w-5 h-5 text-brand-gold" />
                    </div>
                    <h4 className="font-serif text-lg font-bold text-brand-green mb-1">{title}</h4>
                    <p className="text-sm text-brand-green/70 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* PHOTOS RÉELLES */}
        {settings?.gallery_enabled !== false && gallery.length > 0 && (
          <div className="max-w-5xl mx-auto mb-12" data-testid="gallery-section">
            <p className="text-center text-sm font-medium text-brand-green/60 mb-3">Photos réelles de nos sets</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {gallery.map((g) => (
                <div key={g.id} className="aspect-square rounded-2xl overflow-hidden border border-brand-green/15 bg-white">
                  <img src={`${API}/gallery/${g.id}/image`} alt="Set loulav et etrog Zeitrog" loading="lazy" className="w-full h-full object-cover" data-testid={`gallery-photo-${g.id}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-center text-brand-green/60 mb-12">Prix TTC — triés par prix croissant</p>
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {products.map((p) => (
            <div
              key={p.gamme_key}
              data-testid={`gamme-card-${p.gamme_key}`}
              className={`bg-white rounded-2xl overflow-hidden flex flex-col transition-transform hover:-translate-y-1 duration-300 ${
                p.featured ? "ring-2 ring-brand-gold shadow-2xl md:scale-105 relative z-10" : "border border-brand-green/10 shadow-[0_8px_32px_rgba(26,54,38,0.06)]"
              }`}
            >
              {p.featured && (
                <div className="bg-brand-gold text-brand-green text-center py-1.5 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1">
                  <Star className="w-3.5 h-3.5" /> Le plus demandé
                </div>
              )}
              {p.images?.[0] ? (
                <img src={p.images[0]} alt={`Set ${p.name}`} loading="lazy" data-testid={`gamme-image-${p.gamme_key}`} className="h-44 w-full object-cover" />
              ) : (
                <ProductPlaceholder name={p.name} className="h-44" />
              )}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="rounded-full bg-brand-sage px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-green">{p.position_badge}</span>
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <span className="font-serif text-4xl text-brand-gold leading-none">{HEBREW[p.gamme_key]}</span>
                  <h3 className="font-serif text-2xl font-bold text-brand-green">{p.name}</h3>
                </div>
                <div className="mt-4 mb-4">
                  <span className="num text-4xl text-brand-green">{euro(p.price_ttc)}</span>
                  <span className="text-xs text-brand-green/50 ml-2">TTC</span>
                </div>
                <div className="flex gap-2 mt-auto">
                  <Link to={`/commander?gamme=${p.gamme_key}`} data-testid={`reserve-${p.gamme_key}`}
                    className="flex-1 text-center rounded-full py-3 font-bold transition-colors bg-brand-gold text-brand-green hover:bg-brand-goldHover">
                    Réserver
                  </Link>
                  <Link to={`/gamme/${p.gamme_key}`} className="rounded-full border border-brand-green/20 px-4 flex items-center text-sm text-brand-green hover:bg-brand-green/5">
                    Fiche
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-brand-green/10 py-16">
        <div className="brand-container">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-green text-center mb-12">Comment ça marche</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {[[1, "Choisir sa gamme", "Bet, Aleph ou Aleph Aleph — au prix affiché.", Leaf],
              [2, "On vous rappelle", "Nous convenons ensemble du paiement (espèces / Revolut) et de l'horaire.", Phone],
              [3, "Être livré ou retirer", "Livraison offerte en Île-de-France ou retrait dans le 19e.", Truck]].map(([n, t, d, Icon]) => (
              <div key={n} className="text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-brand-green text-brand-gold flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="text-xs uppercase tracking-widest text-brand-gold font-bold mb-1">Étape {n}</div>
                <h3 className="font-serif text-xl font-bold text-brand-green mb-1">{t}</h3>
                <p className="text-sm text-brand-green/70">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERY ZONES */}
      <section className="brand-container py-16">
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-green text-center mb-3">Zones de livraison</h2>
        <p className="text-center text-brand-green/60 mb-10">Livraison offerte partout en Île-de-France — retrait gratuit dans le 19e</p>
        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-3xl bg-brand-green text-white p-8 sm:p-12 text-center shadow-xl overflow-hidden">
            <div className="w-16 h-16 rounded-full bg-brand-gold/20 flex items-center justify-center mx-auto mb-5">
              <Truck className="w-8 h-8 text-brand-gold" />
            </div>
            <h3 className="font-serif text-3xl sm:text-5xl font-bold mb-3">Livraison offerte</h3>
            <p className="text-white/80 text-base sm:text-lg max-w-xl mx-auto">
              Nous livrons <span className="text-brand-gold font-semibold">gratuitement</span> partout dans Paris et toute l'Île-de-France — ou retirez gratuitement dans le 19e.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-7">
              {["Paris 19e", "Paris", "Petite couronne (92·93·94)", "Grande couronne (77·78·91·95)"].map((z) => (
                <span key={z} className="rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-sm">{z}</span>
              ))}
            </div>
          </div>
        </div>
        <p className="text-center text-sm text-brand-green/60 mt-6">Retrait sur place dans le 19e : <span className="font-bold text-brand-green">gratuit</span>. Hors Île-de-France : appelez-nous.</p>
      </section>

      {/* TRUST */}
      <section className="bg-brand-green text-white py-16">
        <div className="brand-container grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[["Produit vérifié", "Chaque set contrôlé avant la remise.", PackageCheck],
            ["Etrog contrôlé", "Pitom intact, droiture et fraîcheur.", Leaf],
            ["Livraison offerte", "Partout en Île-de-France.", Truck],
            ["Rappel personnalisé", "On vous appelle pour tout organiser.", Phone]].map(([t, d, Icon]) => (
            <div key={t}>
              <Icon className="w-8 h-8 text-brand-gold mb-3" />
              <h3 className="font-serif text-xl mb-1">{t}</h3>
              <p className="text-sm text-white/70">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="brand-container py-16 max-w-3xl">
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-green text-center mb-10">Questions fréquentes</h2>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-semibold text-brand-green">{f.q}</AccordionTrigger>
              <AccordionContent className="text-brand-green/70">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
